import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionHead from '../shared/SectionHead';
import Icon from '../shared/Icon';
import { useReveal } from '../../hooks/useReveal';
import useLocalizedPath from '../../hooks/useLocalizedPath';

function LinkCard({ kbd, tag, title, desc, meta }) {
  return (
    <div
      className="reveal"
      style={{
        padding: 28,
        background: 'var(--p2a-dark-panel)',
        border: '1px solid var(--p2a-dark-border)',
        borderRadius: 8,
        display: 'flex', flexDirection: 'column', gap: 20,
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="mono" style={{
          fontSize: 11, fontWeight: 600,
          padding: '4px 8px',
          border: '1px solid var(--p2a-green-400)',
          color: 'var(--p2a-green-400)',
          borderRadius: 4,
          letterSpacing: '0.08em',
        }}>{kbd}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--p2a-dark-fg-3)', letterSpacing: '0.14em' }}>
          {tag}
        </span>
      </div>

      <div>
        <h3 style={{ fontSize: 22, fontWeight: 500, color: 'var(--p2a-dark-fg-1)', letterSpacing: '-0.01em', margin: 0, marginBottom: 10 }}>
          {title}
        </h3>
        <p style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--p2a-dark-fg-2)', margin: 0 }}>{desc}</p>
      </div>

      <div style={{ marginTop: 'auto', borderTop: '1px solid var(--p2a-dark-border)' }}>
        {meta.map((m, i) => (
          <div
            key={m.k}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 0',
              borderBottom: i < meta.length - 1 ? '1px solid var(--p2a-dark-border)' : 'none',
            }}
          >
            <span className="mono" style={{ fontSize: 11, color: 'var(--p2a-dark-fg-4)', letterSpacing: '0.1em' }}>
              {m.k.toUpperCase()}
            </span>
            <span style={{ fontSize: 13, color: 'var(--p2a-dark-fg-1)' }}>{m.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LinkConnector({ label }) {
  return (
    <div
      className="pr-connector reveal"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 24px',
        position: 'relative',
      }}
    >
      <svg width="140" height="60" viewBox="0 0 140 60" style={{ overflow: 'visible' }}>
        <line x1="0" y1="30" x2="140" y2="30" stroke="var(--p2a-dark-border-2)" strokeWidth="1" strokeDasharray="3 4" />
        <circle r="4" fill="var(--p2a-green-500)">
          <animateMotion dur="2.4s" repeatCount="indefinite" path="M0,30 L140,30" />
        </circle>
        <circle r="4" fill="var(--p2a-green-500)" opacity="0.3">
          <animateMotion dur="2.4s" repeatCount="indefinite" begin="0.8s" path="M0,30 L140,30" />
        </circle>
        <path d="M130,24 L140,30 L130,36" stroke="var(--p2a-green-500)" strokeWidth="1.5" fill="none" />
      </svg>
      <div
        className="mono"
        style={{
          position: 'absolute', top: 'calc(50% - 38px)', left: '50%', transform: 'translateX(-50%)',
          fontSize: 10,
          padding: '4px 10px',
          background: 'var(--p2a-dark-bg)',
          border: '1px solid var(--p2a-green-500)',
          color: 'var(--p2a-green-400)',
          borderRadius: 4,
          letterSpacing: '0.12em',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default function PRPixViewerLink() {
  const lp = useLocalizedPath();
  const { t } = useTranslation();
  const ref = useReveal({ stagger: 0.07 });

  const bullets = t('pr.link.bullets', { returnObjects: true });
  const prCard = t('pr.link.pixrow_card', { returnObjects: true });
  const pvCard = t('pr.link.pixviewer_card', { returnObjects: true });

  return (
    <section ref={ref} className="section" style={{ background: 'var(--p2a-dark-bg)', borderBottom: '1px solid var(--p2a-dark-border)' }}>
      <div className="p2-container">
        <SectionHead
          eyebrow={t('pr.link.eyebrow')}
          title={<>{t('pr.link.title_pre')}<span style={{ color: 'var(--p2a-green-400)' }}>{t('pr.link.title_highlight')}</span></>}
          lede={t('pr.link.lede')}
          max={680}
        />

        <div
          className="pr-link-grid"
          style={{ marginTop: 56, display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0, alignItems: 'stretch' }}
        >
          <LinkCard
            kbd="PR"
            tag="PIXROW"
            title={prCard.title}
            desc={prCard.desc}
            meta={prCard.meta}
          />

          <LinkConnector label={t('pr.link.connector_label')} />

          <LinkCard
            kbd="PV"
            tag="PIXVIEWER"
            title={pvCard.title}
            desc={pvCard.desc}
            meta={pvCard.meta}
          />
        </div>

        <div
          className="pr-link-bullets"
          style={{
            marginTop: 56,
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0,
            borderTop: '1px solid var(--p2a-dark-border)',
          }}
        >
          {bullets.map((b, i) => (
            <div
              key={i}
              className="reveal"
              style={{
                padding: '32px 28px',
                borderLeft: i ? '1px solid var(--p2a-dark-border)' : 'none',
              }}
            >
              <div className="mono" style={{ fontSize: 11, color: 'var(--p2a-green-400)', letterSpacing: '0.14em' }}>
                {String(i + 1).padStart(2, '0')}
              </div>
              <h4 style={{ marginTop: 10, marginBottom: 8, fontSize: 17, fontWeight: 500, color: 'var(--p2a-dark-fg-1)' }}>
                {b.t}
              </h4>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--p2a-dark-fg-3)', margin: 0 }}>{b.d}</p>
            </div>
          ))}
        </div>

        <div className="reveal" style={{ marginTop: 40, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to={lp('/pixviewer')} className="btn btn-ghost">
            {t('pr.link.cta_primary')} <Icon.ArrowRight />
          </Link>
          <a
            href="https://api.whatsapp.com/send?phone=5519996542014&text=Ol%C3%A1%2C%20gostaria%20de%20conhecer%20a%20plataforma%20Pix2Agro."
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            {t('pr.link.cta_secondary')} <Icon.ArrowRight />
          </a>
        </div>
      </div>

      <style>{`
        @media (max-width: 920px) {
          .pr-link-grid { grid-template-columns: 1fr !important; }
          .pr-link-grid > .pr-connector { transform: rotate(90deg); justify-self: center; padding: 24px 0 !important; }
          .pr-link-bullets { grid-template-columns: 1fr !important; }
          .pr-link-bullets > div { border-left: none !important; border-top: 1px solid var(--p2a-dark-border) !important; }
          .pr-link-bullets > div:first-child { border-top: none !important; }
        }
      `}</style>
    </section>
  );
}
